import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatBaseLabel } from '../../../utils/baseDisplay';
import { authenticatedFetch } from '../../../auth/auth';

const API_BASE_RAW = (import.meta.env?.VITE_API_BASE_URL || '').trim();
const API_BASE = API_BASE_RAW.replace(/\/+$/, ''); // remove barra final, se houver

const DREComparisonTable = () => {
  // ====== Filtros ======
  const today = new Date();
  const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const ymd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const [tipoData, setTipoData] = useState('dtComp');
  const [de, setDe] = useState(ymd(startMonth));
  const [ate, setAte] = useState(ymd(today));
  const [useA, setUseA] = useState(true);
  const [useB, setUseB] = useState(true);

  // ====== Estados ======
  const [loading, setLoading] = useState(false);

  const [errA, setErrA] = useState('');
  const [errB, setErrB] = useState('');
  const [errConsol, setErrConsol] = useState('');

  const [rowsA, setRowsA] = useState([]);
  const [rowsB, setRowsB] = useState([]);
  const [rowsConsol, setRowsConsol] = useState([]);

  // ====== Helpers UI ======
  const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  const normalizeAccount = (value) => normalize(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
  const grossRevenue = (rows = []) => Number(
    rows.find((row) => normalizeAccount(row.account).includes('RECEITA BRUTA'))?.current || 0
  );
  const revenuePercent = (value, rows = []) => {
    const revenue = Math.abs(grossRevenue(rows));
    return revenue > 0 ? (Math.abs(Number(value || 0)) / revenue) * 100 : 0;
  };

  // ---------- Mapeia RAW (atual x anterior) para linhas ----------
  const normalize = (s) => String(s || '').trim();
  const isCategoryGuess = (label) => {
    const L = normalize(label);
    return !L.startsWith('-');
  };

  const mapRawToRows = (rawNow = []) => {
    const mapNow = new Map(rawNow.map((i) => [normalize(i.dre), Number(i.valor || 0)]));
    return [...mapNow.keys()].map((label) => ({
      id: label.toLowerCase().replace(/[^\w]+/g, '-'),
      account: label,
      current: Number(mapNow.get(label) || 0),
      isCategory: isCategoryGuess(label),
    }));
  };

  // ====== Fetch com verificação de JSON ======
  const getJSON = async (url) => {
    const res = await authenticatedFetch(url, { headers: { Accept: 'application/json' } });
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    if (!ct.includes('application/json')) {
      const txt = await res.text();
      throw new Error(`Resposta não-JSON do servidor: ${txt.slice(0, 120)}`);
    }
    const data = await res.json();
    if (res.status === 401 || data?.ok === false) {
      throw new Error(data?.message || 'Unauthorized');
    }
    return data;
  };

  // ====== Carregadores ======
  const loadRawFor = async (baseKey, d1, d2) => {
    const qs = new URLSearchParams({ de: d1, ate: d2, tipoData }).toString();
    const url = `${API_BASE}/api/dre/${baseKey}?${qs}`;
    const data = await getJSON(url);
    return Array.isArray(data?.raw) ? data.raw : [];
  };

  const loadBase = async (baseKey) => {
    const rawNow = await loadRawFor(baseKey, de, ate);
    return mapRawToRows(rawNow);
  };

  const sumRaw = (raw1 = [], raw2 = []) => {
    const acc = new Map();
    const addAll = (arr) => {
      for (const it of arr) {
        const k = normalize(it.dre);
        const v = Number(it.valor || 0);
        acc.set(k, Number(acc.get(k) || 0) + v);
      }
    };
    addAll(raw1);
    addAll(raw2);
    return [...acc.entries()].map(([dre, valor]) => ({ dre, valor }));
  };

  const loadConsolidado = async () => {
    const [rawANow, rawBNow] = await Promise.all([
      loadRawFor('A', de, ate),
      loadRawFor('B', de, ate),
    ]);
    return mapRawToRows(sumRaw(rawANow, rawBNow));
  };

  const handleReload = async () => {
    setErrA('');
    setErrB('');
    setErrConsol('');

    if (!API_BASE) {
      const msg = 'Defina VITE_API_BASE_URL com a URL do backend (ex.: https://SEU-BACK.onrender/...)';
      setErrA(msg);
      setErrB(msg);
      setErrConsol(msg);
      return;
    }

    setLoading(true);
    try {
      if (!useA && !useB) {
        setRowsA([]);
        setRowsB([]);
        loadConsolidado().then(setRowsConsol).catch((e) => setErrConsol(e.message));
        return;
      }

      setRowsConsol([]);

      if (useA) {
        loadBase('A').then(setRowsA).catch((e) => setErrA(e.message));
      } else {
        setRowsA([]);
      }

      if (useB) {
        loadBase('B').then(setRowsB).catch((e) => setErrB(e.message));
      } else {
        setRowsB([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoData, de, ate, useA, useB]);

  // ====== Exportar CSV (sem "Mês Anterior") ======
  const download = (filename, blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const rowsToCSV = (rows) => {
    const header = ['Conta', 'Mês Atual', '% do faturamento'];
    const lines = [header.join(';')];
    rows.forEach((r) => {
      const percent = revenuePercent(r.current, rows);
      lines.push([r.account, formatCurrency(r.current), `${percent.toFixed(1)}%`].join(';'));
    });
    return lines.join('\n');
  };

  const handleExportCSV = () => {
    const titulo = `DRE_${tipoData}_${de}_a_${ate}`;
    const parts = [];

    if (!useA && !useB && rowsConsol.length) {
      parts.push(formatBaseLabel('ALL'));
      parts.push(rowsToCSV(rowsConsol));
    } else {
      if (useA && rowsA.length) {
        parts.push(formatBaseLabel('A'));
        parts.push(rowsToCSV(rowsA));
      }
      if (useB && rowsB.length) {
        parts.push('');
        parts.push(formatBaseLabel('B'));
        parts.push(rowsToCSV(rowsB));
      }
    }

    const csv = '\ufeff' + parts.join('\n');
    download(`${titulo}.csv`, new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  };

  // ====== Exportar PDF (sem "Mês Anterior") ======
  const tableHTML = (title, rows) => `
    <h3 style="margin:16px 0 8px 0;font-family:Inter,system-ui,sans-serif;">${title}</h3>
    <table style="width:100%;border-collapse:collapse;font-family:Inter,system-ui,sans-serif;font-size:12px;">
      <thead>
        <tr>
          <th style="text-align:left;padding:8px;border:1px solid #eee;background:#faf7f5;">Conta</th>
          <th style="text-align:right;padding:8px;border:1px solid #eee;background:#faf7f5;">Mês Atual</th>
          <th style="text-align:right;padding:8px;border:1px solid #eee;background:#faf7f5;">% do faturamento</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map((r) => {
            const percent = revenuePercent(r.current, rows);
            return `<tr>
              <td style="padding:8px;border:1px solid #eee;${r.isCategory ? 'font-weight:600;background:#fcf9f6;' : ''}">${r.account}</td>
              <td style="padding:8px;border:1px solid #eee;text-align:right;">${formatCurrency(r.current)}</td>
              <td style="padding:8px;border:1px solid #eee;text-align:right;">${percent.toFixed(1)}%</td>
            </tr>`;
          })
          .join('')}
      </tbody>
    </table>
  `;

  const handleExportPDF = () => {
    const titulo = `DRE_${tipoData}_${de}_a_${ate}`;
    const head = `
      <meta charset="utf-8" />
      <title>${titulo}</title>
      <style>
        @page { size: A4; margin: 16mm; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      </style>
    `;
    const header = `
      <div style="font-family:Inter,system-ui,sans-serif;margin-bottom:12px;">
        <div style="font-size:18px;font-weight:700;">DRE - Demonstração do Resultado</div>
        <div style="font-size:12px;color:#6b6b6b;">Tipo de Data: ${tipoData} • Período: ${de} a ${ate}</div>
      </div>
    `;

    const sections = [];
    if (!useA && !useB && rowsConsol.length) {
      sections.push(tableHTML(formatBaseLabel('ALL'), rowsConsol));
    } else {
      if (useA && rowsA.length) sections.push(tableHTML(formatBaseLabel('A'), rowsA));
      if (useB && rowsB.length) sections.push(tableHTML(formatBaseLabel('B'), rowsB));
    }
    if (sections.length === 0)
      sections.push('<p style="font-size:12px;">Sem dados para exportar.</p>');

    const html = `
      <!doctype html>
      <html>
        <head>${head}</head>
        <body>
          ${header}
          ${sections.join('<div style="height:12px;"></div>')}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function(){ window.close(); }, 300);
            };
          </script>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) {
      download(`${titulo}.html`, blob);
    }
  };

  // ====== UI: Filtros ======
  const FilterBar = useMemo(
    () => (
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-muted-foreground mb-1">Tipo de Data</label>
          <select
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
            value={tipoData}
            onChange={(e) => setTipoData(e.target.value)}
          >
            <option value="dtComp">Competência</option>
            <option value="dtPgto">Pagamento</option>
            <option value="dtVenc">Vencimento</option>
          </select>
        </div>

        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-muted-foreground mb-1">De</label>
          <input
            type="date"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
            value={de}
            onChange={(e) => setDe(e.target.value)}
          />
        </div>

        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs text-muted-foreground mb-1">Até</label>
          <input
            type="date"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
            value={ate}
            onChange={(e) => setAte(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={useA} onChange={(e) => setUseA(e.target.checked)} />
            {formatBaseLabel('A')}
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={useB} onChange={(e) => setUseB(e.target.checked)} />
            {formatBaseLabel('B')}
          </label>
        </div>

        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={handleExportCSV}
          >
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            iconPosition="left"
            onClick={handleExportPDF}
          >
            PDF
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={handleReload}
            loading={loading}
          >
            Atualizar
          </Button>
        </div>
      </div>
    ),
    [tipoData, de, ate, useA, useB, loading, rowsA, rowsB, rowsConsol]
  );

  // ====== UI: Tabela (sem "Mês Anterior") ======
  const renderTable = (title, rows, err) => (
    <div className="mt-6">
      <h4 className="font-heading-semibold text-card-foreground mb-2">{title}</h4>
      {err ? <div className="text-error text-sm mb-2">{err}</div> : null}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left p-4 font-heading-semibold text-sm text-card-foreground">Conta</th>
              <th className="text-right p-4 font-heading-semibold text-sm text-card-foreground">Mês Atual</th>
              <th className="text-right p-4 font-heading-semibold text-sm text-card-foreground">% do faturamento</th>
              <th className="w-12 p-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const percent = revenuePercent(r.current, rows);
              return (
                <tr
                  key={r.id}
                  className={`border-b border-border hover:bg-muted/30 transition-smooth ${
                    r.isCategory ? 'bg-muted/20' : ''
                  }`}
                >
                  <td className="p-4">
                    <span
                      className={`${
                        r.isCategory ? 'font-heading-semibold' : 'font-body-normal'
                      } text-sm text-card-foreground`}
                    >
                      {r.account}
                    </span>
                  </td>
                  <td className="text-right p-4 font-data text-sm text-card-foreground">
                    {formatCurrency(r.current)}
                  </td>
                  <td className="text-right p-4 font-body-medium text-sm text-card-foreground">
                    <div className="flex items-center justify-end space-x-1">
                      <span>{percent.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="p-4"></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-lg border border-border card-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-heading-semibold text-lg text-card-foreground">
                DRE - Demonstração do Resultado
              </h3>
              <p className="text-sm text-muted-foreground">Comparativo mensal de receitas e despesas</p>
            </div>
          </div>
        </div>

        <div className="mt-6">{FilterBar}</div>
      </div>

      {/* Tabelas por base ou consolidado */}
      <div className="p-6">
        {!useA && !useB ? (
          renderTable(formatBaseLabel('ALL'), rowsConsol, errConsol)
        ) : (
          <>
            {useA && renderTable(formatBaseLabel('A'), rowsA, errA)}
            {useB && renderTable(formatBaseLabel('B'), rowsB, errB)}
          </>
        )}
      </div>
    </div>
  );
};

export default DREComparisonTable;
